import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-lg font-bold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-medium hover:shadow-strong",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-2 border-border bg-transparent hover:bg-muted",
        secondary: "bg-gradient-accent text-secondary-foreground hover:shadow-glow shadow-medium transform hover:scale-105",
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-secondary underline-offset-4 hover:underline",

        // --- NEW HERO VARIANT ---
        // Solid, bright button with a subtle shadow, matching the "Explore Portfolio" button
        hero: "bg-cyan-400 text-white shadow-lg shadow-cyan-400/40 hover:bg-cyan-300 hover:shadow-xl hover:shadow-cyan-300/60 transform hover:scale-[1.03]",

        // --- NEW OUTLINE HERO VARIANT ---
        // Semi-transparent with a glowing border, matching the "For Founders" button
        'outline-hero': "bg-blue-950/40 text-white backdrop-blur-sm border border-white/40 shadow-[0_0_8px_rgba(255,255,255,0.3)] hover:shadow-[0_0_15px_rgba(255,255,255,0.5)] hover:border-white/60 transform hover:scale-[1.03]",
      },
      size: {
        // --- UPDATED PADDING ---
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-md px-4",
        // Using padding instead of fixed height for better text wrapping
        lg: "px-8 py-4",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default", // We'll specify lg in the Hero component
    },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };